import { useState, type ReactNode } from 'react'
import { cn } from '@/utils/cn'

interface TooltipProps {
  children: ReactNode
  content: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

export function Tooltip({ children, content, position = 'top', className }: TooltipProps) {
  const [visible, setVisible] = useState(false)

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  }

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-white/[0.9] dark:border-t-[#1f1f1f]',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-white/[0.9] dark:border-b-[#1f1f1f]',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-white/[0.9] dark:border-l-[#1f1f1f]',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-white/[0.9] dark:border-r-[#1f1f1f]',
  }

  return (
    <div
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div
          className={cn(
            'absolute z-50 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap',
            'bg-[#1f1f1f] text-white shadow-lg border border-white/[0.08]',
            'pointer-events-none',
            positionClasses[position]
          )}
          style={{ animation: 'tooltipFadeIn 0.12s ease-out' }}
        >
          {content}
          <span
            className={cn(
              'absolute w-0 h-0 border-4 border-transparent',
              arrowClasses[position]
            )}
          />
        </div>
      )}
    </div>
  )
}
